import React, { useEffect, useRef, useMemo } from "react";

// Redux
import { connect, useDispatch } from "react-redux";
import { SET_IS_ADMIN } from "../../redux/Types";

// Apollo GraphQL
import { useQuery } from "@apollo/client";
import { SITE_DATA } from "../../@global/queries";
import useSiteAccess from "./useSiteAccess";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";

// CSS & MUI
import GlobalStyles from "../../@global/styles";
import { ThemeProvider as ScThemeProvider } from "styled-components";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import createMuiTheme from "@material-ui/core/styles/createMuiTheme";
import responsiveFontSizes from "@material-ui/core/styles/responsiveFontSizes";

// Utils
import WebFont from "webfontloader";
import { default_theme_obj } from "../../@global/const";
import { getSiteId } from "../../@utils/general";
import logger from "../../@global/logger";
import { useKeycloak } from "@react-keycloak/web";

// Components
import SiteLayout from "../SiteLayout";
import {
  HeaderSkeleton,
  PageSkeleton,
  FooterSkeleton,
} from "../../@common/LoadingSkeleton";

const Site = ({ bearer_token }) => {
  const [keycloak, initialized] = useKeycloak();
  const dispatch = useDispatch();

  const fonts = useRef("");

  const site_id = getSiteId();
  const access = useSiteAccess(keycloak, initialized, bearer_token, site_id);

  const { data, loading, error, refetch } = useQuery(SITE_DATA, {
    skip: !access.ready,
    variables: { id: site_id },
    onCompleted: (data) => {
      const site_item = (data || {}).siteItem || {};
      dispatch({ type: SET_IS_ADMIN, payload: site_item.is_admin });
    },
    onError: (error) => logger.error(error),
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // Synchronize admin privileges (is_admin) to Redux whenever site data is updated
  useEffect(() => {
    if (data && data.siteItem && typeof data.siteItem.is_admin !== "undefined") {
      console.log('Site data is updated!');
      dispatch({ type: SET_IS_ADMIN, payload: data.siteItem.is_admin });
    }
  }, [data, dispatch]);

  // Handle theme object only when site_data.theme changes
  const site_data = (data || {}).siteItem || {};

  const theme_obj = useMemo(() => {
    const theme = site_data.theme || {};
    return Object.entries(theme).length === 0
      ? default_theme_obj
      : (
        // Ignore `ariaCore` themes (only used for ARIA core whitelabel)
        !theme.ariaCore || theme.ariaCore.length === 0
        ? theme
        : default_theme_obj
      );
  }, [site_data.theme]);

  // Create the MUI theme only when theme_obj changes
  const theme = useMemo(() => {
    return responsiveFontSizes(createMuiTheme(theme_obj));
  }, [theme_obj]);

  // Load fonts only when theme changes
  useEffect(() => {
    const font_to_load = theme.typography.fontFamily
      .split(",")[0]
      .replace(/"/g, "")
      .trim();

    if (fonts.current !== font_to_load) {
      fonts.current = font_to_load;

      const fonts_config = {
        classes: false,
        timeout: 3000,
        loading: () => logger.debug(`${font_to_load} font is loading ...`),
        active: () => logger.debug(`${font_to_load} font is loaded`),
        inactive: () => logger.debug(`${font_to_load} Font is inactive`),
        google: { families: [`${font_to_load}:300,400,500,700`] },
      };

      logger.debug(`Load ${font_to_load} font`);
      WebFont.load(fonts_config);
    }
  }, [theme]);

  // Memoize layout to avoid re-renders
  const layout = useMemo(() => <SiteLayout home_uri={site_data.home_uri} />, [site_data.home_uri]);

  return (
    <MuiThemeProvider theme={theme}>
      <ScThemeProvider theme={theme}>
        <GlobalStyles theme={theme} />
        {access.error || (access.ready && !loading && (error || !data || !data.siteItem)) ? (
          <div role="alert">
            <Typography>Unable to load this site. Please try again.</Typography>
            <Button onClick={access.error ? access.retry : () => refetch().catch(logger.error)}>Retry</Button>
          </div>
        ) : !access.ready || (loading && !data) ? (
          <>
            <HeaderSkeleton />
            <div style={{ paddingTop: 60 }}>
              <PageSkeleton />
            </div>
            <FooterSkeleton />
          </>
        ) : layout}
      </ScThemeProvider>
    </MuiThemeProvider>
  );
};

export default connect((state) => ({ bearer_token: state.User.token }))(Site);

Site.whyDidYouRender = {
  logOnDifferentValues: false,
};
